/**
 * Template-declared docs shown on request tickets (topic + link).
 *
 * Sources (first wins):
 * 1. payload.meta.ticketDocs — snapshot on the ticket
 * 2. Template entity annotation ironphoenix.io/ticket-docs — JSON array
 * 3. Built-in fallback for GitLab Access Request templates (SSO PDF)
 */

export const TICKET_DOCS_ANNOTATION = 'ironphoenix.io/ticket-docs';

export type TicketDocWhen = 'always' | 'open' | 'closed';

export type TicketDocAttachment = {
  /** Section heading shown on the ticket (topic). */
  topic: string;
  /** Link label / button text. */
  title: string;
  /** Absolute URL or app-relative path (e.g. /GitLab-SSO-Access-Instructions.pdf). */
  url: string;
  /** Optional short body under the topic. */
  description?: string;
  /** When to show: always | open | closed (default always). */
  when?: TicketDocWhen;
};

const GITLAB_SSO_FALLBACK: TicketDocAttachment[] = [
  {
    topic: 'Ticket closed but GitLab access is not appearing',
    title: 'GitLab SSO Access Instructions (PDF)',
    url: '/GitLab-SSO-Access-Instructions.pdf',
    description:
      'If provisioning completed but you still cannot open the requested group or project in GitLab, complete SSO activation (same Incognito window), then retry. After Authorize SSO you should see a 404 on gitlab.com/iron-mountain1 — keep a screenshot if support asks. Still blocked? Contact devsecops.groups@ironmountain.com with this request id, the GitLab path, and the 404 screenshot.',
    when: 'closed',
  },
];

function asDoc(raw: unknown): TicketDocAttachment | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const o = raw as Record<string, unknown>;
  const topic = typeof o.topic === 'string' ? o.topic.trim() : '';
  const title = typeof o.title === 'string' ? o.title.trim() : '';
  const url = typeof o.url === 'string' ? o.url.trim() : '';
  if (!topic || !title || !url) return undefined;
  const description =
    typeof o.description === 'string' ? o.description.trim() : undefined;
  let when: TicketDocWhen | undefined;
  if (o.when === 'always' || o.when === 'open' || o.when === 'closed') {
    when = o.when;
  }
  return { topic, title, url, description, when };
}

export function parseTicketDocsJson(raw: string | undefined): TicketDocAttachment[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.map(asDoc).filter((d): d is TicketDocAttachment => Boolean(d));
  } catch {
    return [];
  }
}

/** Docs snapshotted on the ticket under payload.meta.ticketDocs */
export function ticketDocsFromPayload(
  payload: Record<string, unknown> | undefined,
): TicketDocAttachment[] {
  const meta = payload?.meta;
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return [];
  const docs = (meta as Record<string, unknown>).ticketDocs;
  if (!Array.isArray(docs)) return [];
  return docs.map(asDoc).filter((d): d is TicketDocAttachment => Boolean(d));
}

export function isGitLabAccessRequestRef(
  requestName: string | undefined,
  templateEntityRef: string | undefined,
): boolean {
  const name = (requestName || '').toLowerCase();
  const ref = (templateEntityRef || '').toLowerCase();
  return name.includes('gitlab-access') || ref.includes('gitlab-access-request');
}

/**
 * Resolve docs for a ticket: payload snapshot → template annotation → GitLab fallback.
 */
export function resolveTicketDocs(options: {
  payload?: Record<string, unknown>;
  requestName?: string;
  templateEntityRef?: string;
  templateAnnotationJson?: string;
}): TicketDocAttachment[] {
  const fromPayload = ticketDocsFromPayload(options.payload);
  if (fromPayload.length > 0) return fromPayload;

  const fromAnnotation = parseTicketDocsJson(options.templateAnnotationJson);
  if (fromAnnotation.length > 0) return fromAnnotation;

  if (
    isGitLabAccessRequestRef(options.requestName, options.templateEntityRef)
  ) {
    return GITLAB_SSO_FALLBACK;
  }
  return [];
}

export function filterTicketDocsForStatus(
  docs: TicketDocAttachment[],
  status: 'open' | 'in-progress' | 'closed',
  opts?: { rejected?: boolean; cancelled?: boolean },
): TicketDocAttachment[] {
  if (opts?.rejected || opts?.cancelled) {
    return docs.filter(d => (d.when ?? 'always') === 'always');
  }
  const closed = status === 'closed';
  return docs.filter(d => {
    const when = d.when ?? 'always';
    if (when === 'always') return true;
    if (when === 'closed') return closed;
    if (when === 'open') return !closed;
    return true;
  });
}
